/**
 * index.js
 *
 * Main application entry point and routing configuration.
 * Defines all routes for the Gifted platform including home, feed,
 * profiles, gift claiming, and payment confirmation pages.
 *
 * The /send route uses the background location pattern: the drawer renders
 * on top of whatever page the user came from (or the feed as fallback).
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import './index.css';
import App from './App';
import PaymentConfirmation from './PaymentConfirmation';
import ClaimGift from './ClaimGift';
import ClaimComplete from './ClaimComplete';
import UserFeed from './UserFeed';
import UserProfile from './UserProfile';
import UserSettings from './UserSettings';
import CreatePost from './CreatePost';
import AcceptPool from './AcceptPool';
import Signup from './Signup';
import Login from './Login';
import ForgotPassword from './ForgotPassword';
import StripeOnboardingReturn from './StripeOnboardingReturn';
import PayPalReturn from './PayPalReturn';
import PayPalCancel from './PayPalCancel';
import About from './About';
import Faq from './Faq';
import Feedback from './Feedback';
import BetaBanner from './BetaBanner';
import VerifyEmailBanner from './VerifyEmailBanner';
import ResetPassword from './ResetPassword';
import VerifyEmail from './VerifyEmail';
import TermsOfService from './TermsOfService';
import PrivacyPolicy from './PrivacyPolicy';
import AdminDashboard from './AdminDashboard';
import GiveGift from './GiveGift';
import { AuthProvider } from './AuthContext';

function AppRoutes() {
  const location = useLocation();
  // background is set when navigating to /send from another page
  const background = location.state?.background;

  return (
    <>
      {/* Render the background page (or normal route when not on /send) */}
      <Routes location={background || location}>
        <Route path="/" element={<UserFeed />} />
        <Route path="/send" element={<UserFeed />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/settings" element={<UserSettings />} />
        <Route path="/create-post" element={<CreatePost />} />
        <Route path="/confirmation" element={<PaymentConfirmation />} />
        <Route path="/claim/:claimToken" element={<ClaimGift />} />
        <Route path="/claim/:claimToken/complete" element={<ClaimComplete />} />
        <Route path="/claim-complete" element={<ClaimComplete />} />
        <Route path="/accept-pool/:token" element={<AcceptPool />} />
        <Route path="/profile/:userId" element={<UserProfile />} />
        <Route path="/feed" element={<UserFeed />} />
        <Route path="/about" element={<About />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/stripe/return" element={<StripeOnboardingReturn />} />
        <Route path="/stripe/refresh" element={<StripeOnboardingReturn />} />
        <Route path="/payment/paypal/return" element={<PayPalReturn />} />
        <Route path="/payment/paypal/cancel" element={<PayPalCancel />} />
        <Route path="/payment-confirmation" element={<PaymentConfirmation />} />
      </Routes>

      {/* Gift drawer — rendered as overlay when on /send */}
      {location.pathname === '/send' && <GiveGift />}
    </>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <AuthProvider>
      <BrowserRouter>
        <BetaBanner />
        <VerifyEmailBanner />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  </React.StrictMode>
);
